import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import { SidebarNavContent } from './SidebarNavContent';
import OutrisFaviconDark from '../assets/outris-square-dark.svg';
import OutrisFaviconLight from '../assets/outris-square-light.svg';

interface MobileNavDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({ isOpen, onClose }) => {
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
            <button
                type="button"
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                aria-label="Close navigation menu"
                onClick={onClose}
            />
            <aside
                className={clsx(
                    'absolute left-0 top-0 h-full w-[min(18rem,85vw)] bg-card border-r border-border/60 shadow-xl',
                    'flex flex-col animate-in slide-in-from-left duration-200'
                )}
            >
                <div className="flex items-center justify-between px-4 h-16 border-b border-border/60">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white dark:bg-brand-ink ring-1 ring-primary/30">
                            <img src="/brand/cryptoflow-icon-light.svg" alt="CryptoFlow" className="block dark:hidden h-5 w-5" />
                            <img src="/brand/cryptoflow-icon-dark.svg" alt="CryptoFlow" className="hidden dark:block h-5 w-5" />
                        </div>
                        <span className="text-sm font-semibold text-foreground">CryptoFlow</span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close navigation menu"
                        className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md hover:bg-accent transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden styled-scrollbar page-scrollbar p-4">
                    <SidebarNavContent onNavigate={onClose} />
                </div>

                <div className="border-t border-primary/30 p-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <img src={OutrisFaviconLight} alt="Outris" className="block dark:hidden h-6 w-6" />
                    <img src={OutrisFaviconDark} alt="Outris" className="hidden dark:block h-6 w-6" />
                    <span>Powered by <span className="font-medium text-foreground/80">Outris</span></span>
                </div>
            </aside>
        </div>
    );
};
